import { motion } from "framer-motion";
import { Newspaper, ExternalLink, Globe, BookOpen, Send, Zap, Mail } from "lucide-react";
import { Layout } from "@/components/Layout";
import { useLanguage } from "@/lib/LanguageContext";
import { springPresets, staggerContainer, staggerItem } from "@/lib/motion";
import { EXTERNAL_LINKS } from "@/lib/index";

export default function ViewpointPage() {
  const { t } = useLanguage();

  const TOPICS = [
    "B2B SaaS Growth Marketing",
    "AI & Automation in Business",
    "Technical SEO & Demand Gen",
    "GTM Systems Architecture",
    "LinkedIn 360Brew Insights",
    "ROI-driven Strategy",
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-12">
        {/* ── Enhanced Hero Section with Banner ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="relative rounded-[2rem] overflow-hidden border border-border/40 group shadow-2xl"
          style={{ background: "oklch(0.12 0.02 235)" }}
        >
          {/* Hero Banner Image */}
          <div className="absolute inset-0 z-0">
            <img 
              src="/viewpoint_banner.jpg" 
              alt="The Viewpoint Banner" 
              className="w-full h-full object-cover opacity-40 group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
          </div>

          {/* Content Over Banner */}
          <div className="relative z-10 p-8 sm:p-12 flex flex-col items-center text-center space-y-8">
            {/* Logo Badge */}
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ ...springPresets.gentle, delay: 0.2 }}
              className="w-48 h-24 sm:w-64 sm:h-32 bg-black/80 flex items-center justify-center ring-4 ring-primary/40 p-4 shadow-2xl rounded-2xl overflow-hidden"
            >
              <img src="/viewpoint_logo.png" alt="The Viewpoint Logo" className="w-full h-full object-contain" />
            </motion.div>

            <div className="space-y-4 max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/20 text-primary text-xs font-bold tracking-widest uppercase border border-primary/30">
                <Newspaper className="w-3.5 h-3.5" />
                {t("viewpoint.heroBadge")}
              </div>
              <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight">
                {t("viewpoint.title")}
              </h1>
              <p className="text-lg sm:text-xl text-primary font-bold">
                {t("viewpoint.subtitle")}
              </p>
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed max-w-2xl mx-auto font-medium">
                {t("viewpoint.description")}
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <a
                href={EXTERNAL_LINKS.VIEWPOINT}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-primary text-primary-foreground text-sm font-black hover:bg-primary/90 transition-all shadow-xl shadow-primary/25"
              >
                <Mail className="w-5 h-5" />
                {t("common.subscribeLinkedIn")}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </motion.div>

        {/* Content Section */}
        <div className="grid lg:grid-cols-3 gap-8">
          <motion.section
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
            className="lg:col-span-2 rounded-3xl border border-border/60 bg-card p-8 sm:p-10 space-y-6 shadow-xl shadow-black/20"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">{t("viewpoint.aboutTitle")}</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                {t("viewpoint.aboutText1")}
              </p>
              <p>
                {t("viewpoint.aboutText2")}
              </p>
              <p>
                {t("viewpoint.aboutText3")}
              </p>
            </div>
          </motion.section>

          {/* Topics & Links */}
          <div className="space-y-8">
            {/* Main Topics */}
            <motion.section
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.15 }}
              className="rounded-3xl border border-border/60 bg-card p-6 space-y-4 shadow-xl shadow-black/20"
            >
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" />
                {t("viewpoint.topics")}
              </h2>
              <div className="flex flex-wrap gap-2">
                {TOPICS.map((topic, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-xl bg-primary/10 text-primary text-xs font-semibold border border-primary/20">
                    {topic}
                  </span>
                ))}
              </div>
            </motion.section>

            {/* CTA */}
            <motion.section
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.2 }}
              className="rounded-3xl border border-primary/20 bg-primary/5 p-6 space-y-4 shadow-xl shadow-primary/5"
            >
              <div className="flex items-center gap-2 text-primary">
                <Send className="w-5 h-5" />
                <h2 className="text-lg font-bold">{t("viewpoint.joinTitle")}</h2>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("viewpoint.joinDesc")}
              </p>
              <a
                href={EXTERNAL_LINKS.VIEWPOINT}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-all"
              >
                {t("common.goNewsletter")}
                <ExternalLink className="w-4 h-4" />
              </a>
            </motion.section>
          </div>
        </div>
      </div>
    </Layout>
  );
}
